/**
 * config.js — central runtime configuration for the frontend.
 *
 * WS_BRIDGE_URL: address of the Python WS bridge (ws_bridge.py).
 * Resolved dynamically so the app works both locally and on a remote server:
 *   1. VITE_WS_URL env var (full override, e.g. "wss://radar.example.com/ws")
 *   2. otherwise built from the page host: ws(s)://<current-host>:8765
 */
const WS_PORT = import.meta.env.VITE_WS_PORT || '8765';
const WS_PROTO = window.location.protocol === 'https:' ? 'wss' : 'ws';

// VITE_WS_URL may be:
//   • absolute  ("wss://radar.example.com/ws")        → used as-is
//   • path-only ("/ws", behind an nginx reverse proxy) → turned into
//                ws(s)://<current-host>/ws  (no port → goes through :80/:443)
// Otherwise we fall back to the dev default ws://<host>:8765.
function resolveWsUrl() {
  const v = import.meta.env.VITE_WS_URL;
  if (v && /^wss?:\/\//i.test(v)) return v;            // already absolute
  if (v && v.startsWith('/')) return `${WS_PROTO}://${window.location.host}${v}`;
  return `${WS_PROTO}://${window.location.hostname}:${WS_PORT}`;
}

export const WS_BRIDGE_URL = resolveWsUrl();

// Auth + billing API (backend-node). JWT is stored in localStorage.
const AUTH_PORT = import.meta.env.VITE_AUTH_PORT || '8090';

export const AUTH_API_URL =
  import.meta.env.VITE_AUTH_URL ||
  `${window.location.protocol === 'https:' ? 'https' : 'http'}://${window.location.hostname}:${AUTH_PORT}`;

// Optional OAuth widget config (when present, real widgets mount; otherwise the
// modal falls back to a manual dev sign-in form the backend also accepts).
// TELEGRAM_CLIENT_ID = numeric bot ID from BotFather → Bot Settings → Web Login
export const TELEGRAM_CLIENT_ID = import.meta.env.VITE_TELEGRAM_CLIENT_ID || '';
export const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || '';
export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Owner decision (merge): order execution lives in the desktop Trade
// Terminal now. The web app is analysis + position sizing + "my trades /
// my bots". Any exchange write (order, algoOrder, leverage, margin) is
// blocked client-side; reads (balance, positions) still work so the
// calculator and the position display keep functioning.
// Set VITE_SITE_TRADING=1 only for a local dev run that deliberately needs it.
export const SITE_TRADING_ENABLED = import.meta.env.VITE_SITE_TRADING === '1';

// "Open in terminal" (quantum:// deep link) — where to send a visitor whose
// OS did not hand the link to an installed terminal (WEB_TERMINAL_UNIFICATION
// B2). Override per-env; defaults to the public download page.
export const TERMINAL_DOWNLOAD_URL =
  import.meta.env.VITE_TERMINAL_DOWNLOAD_URL || 'https://quan.trading/download';
export const TRADING_MOVED_MESSAGE =
  'Order execution has moved to the Quantum Terminal desktop app. The web app is for analysis and position sizing.';
